import type { FC } from "react"
import { useState, Fragment, useEffect, useRef } from "react"
import { Popover, Transition } from "@headlessui/react"
import HeaderPopoverLink from "~/components/header/HeaderPopoverLink"
import HeaderLink from "~/components/header/HeaderLink"
import Link from "~/components/Link"
import Logo from "~/icons/Logo"
import Menu from "~/icons/Menu"
import Search from "~/icons/Search"
import Loupe from "~/icons/Loupe"
import ChevronDown from "~/icons/ChevronDown"
import type { MobileMenuState } from "~/components/Header"
import HeaderLocationsPopover from "~/components/header/HeaderLocationsPopover"
import HeaderActivitiesPopover from "~/components/header/HeaderActivitiesPopover"
import type {
  WithContentfulHeaderLocationFragment,
  WithLinkFragment as LinkEntry
} from "~/contentful"
import SearchInput from "../search/SearchInput"
import { Form, useNavigate, useLocation } from "@remix-run/react"
import { searchPath } from "~/helpers/routes"
import { LocationSelectPopover } from "../design-system/organisms/LocationSelectPopover"
import {
  getHeaderLocationLogoUrl,
  getLocationCardHeadline
} from "~/helpers/location"
import { useLocationSelectNavigation } from "~/hooks/useLocationSelectNavigation"
import { useHeaderLocationPicker } from "~/contexts/HeaderLocationPickerContext"
import ResponsiveContentfulImage from "../ResponsiveContentfulImage"
import { LogoutButton } from "~/components/auth/LogoutButton"
import classNames from "classnames"

/** Same as `theme.screens.md` in tailwind.config.ts */
const MD_MEDIA_QUERY = "(min-width: 768px)"

const HeaderColorBarV2: FC<{
  setMobileMenuState: (state: MobileMenuState) => void
  preloadMobileMenuImages: () => void
  showSearch: boolean
  bookingMode?: boolean
  customBackgroundColor?: string
  locationPages: WithContentfulHeaderLocationFragment[]
  activityLinks?: LinkEntry[]
  loggedIn?: boolean
  locationSlug?: string | null
}> = ({
  setMobileMenuState,
  preloadMobileMenuImages,
  showSearch,
  bookingMode = false,
  customBackgroundColor,
  locationPages,
  activityLinks = [],
  loggedIn,
  locationSlug
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [queryValue, setQueryValue] = useState("")
  const colorBarRootRef = useRef<HTMLDivElement>(null)
  const desktopLocationBtnRef = useRef<HTMLButtonElement>(null)
  const mobileLocationBtnRef = useRef<HTMLButtonElement>(null)

  const headerLocationPicker = useHeaderLocationPicker()

  const {
    effectiveLocationName,
    getLocationSelectDestination,
    cmsLocationSlug,
    publicLocationSiteSlug
  } = useLocationSelectNavigation(locationPages, locationSlug)

  useEffect(() => {
    if (!headerLocationPicker || locationPages.length === 0) return undefined

    const openHeaderLocationPopover = () => {
      colorBarRootRef.current?.scrollIntoView({ block: "start" })
      queueMicrotask(() => {
        const isMdUp = window.matchMedia(MD_MEDIA_QUERY).matches
        const button = isMdUp
          ? desktopLocationBtnRef.current
          : mobileLocationBtnRef.current

        if (button?.getAttribute("aria-expanded") !== "true") {
          button?.click()
        }
      })
    }

    return headerLocationPicker.registerHeaderLocationPickerOpener(
      openHeaderLocationPopover
    )
  }, [headerLocationPicker, locationPages.length])

  const selectedLocation = locationPages.find(
    loc => loc.name === effectiveLocationName
  )

  const staticHeaderLogo = getHeaderLocationLogoUrl(
    selectedLocation?.name ?? effectiveLocationName
  )
  const contentfulLogoUrl = selectedLocation?.logo?.url ?? null
  const headerBrandLogoSrc = staticHeaderLogo ?? contentfulLogoUrl

  const headerBrandLogo = !headerBrandLogoSrc ? (
    <Logo className="h-9 text-white" />
  ) : staticHeaderLogo ? (
    <img
      src={staticHeaderLogo}
      alt={selectedLocation?.name ?? "Camp"}
      className={classNames("w-auto object-contain", {
        "h-11": selectedLocation?.name === "Charlotte",
        "h-9": selectedLocation?.name !== "Charlotte"
      })}
    />
  ) : (
    <ResponsiveContentfulImage
      src={contentfulLogoUrl!}
      alt={selectedLocation?.name ?? "Camp"}
      className="h-9 w-auto object-contain"
      sizeMobile="72px"
      sizeDesktop="72px"
      lazyLoad={false}
    />
  )

  const shortName = getLocationCardHeadline(effectiveLocationName)
  const locationSelectButtonShortName =
    shortName === "New York City" ? "NYC" : shortName
  const displayLocationName = `CAMP ${locationSelectButtonShortName}`

  const goToSearchPage = (query: string) => {
    navigate(`${searchPath()}?query=${query.length > 0 ? query : queryValue}`)
  }

  const bookingSignInForm = loggedIn ? null : (
    <Form method="post" action="/authorize" className="inline">
      <input type="hidden" name="form_type" value="customer_login" />
      <input
        type="hidden"
        name="returnTo"
        value={location.pathname + location.search + location.hash}
      />
      <button
        type="submit"
        className="underline font-semibold text-membership-yellow"
      >
        Sign in here
      </button>
    </Form>
  )

  const bookingSignInDesktop = bookingMode && (
    <span className="text-white text-sm">
      {loggedIn ? (
        <LogoutButton ctaText="Sign out" color="#dee950" underline />
      ) : (
        <>
          {"Are you a Top Bunk Member? "}
          {bookingSignInForm}
        </>
      )}
    </span>
  )

  const bookingSignInMobile = bookingMode && (
    <span className="text-white text-base">
      {loggedIn ? (
        <LogoutButton ctaText="Sign out" color="#dee950" underline />
      ) : (
        <>
          {"Are you a Top Bunk Member? "}
          {bookingSignInForm}
        </>
      )}
    </span>
  )

  return (
    <>
      <div
        ref={colorBarRootRef}
        className="relative mx-auto flex h-[60px] max-w-camp-container-limit items-center bg-camp-primary px-6 shadow-lg lg:h-auto"
        style={{ backgroundColor: customBackgroundColor }}
      >
        <div className="flex w-full max-w-camp-container items-center mx-auto">
          <div className="flex flex-1 items-center gap-4">
            <Link
              className="hidden lg:block"
              to="/"
              title="Home"
              aria-label="Camp Home"
            >
              {headerBrandLogo}
            </Link>

            {locationPages.length > 0 && (
              <Popover className="hidden md:block">
                {({ open }) => (
                  <>
                    <Popover.Button
                      ref={desktopLocationBtnRef}
                      className="flex items-center gap-1 text-white text-base transition-opacity p-2 active:scale-[98%]"
                    >
                      {displayLocationName}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      />
                    </Popover.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute left-0 top-full z-40 overflow-hidden rounded-b-lg bg-white shadow-[0px_0px_20px_rgba(0,0,0,0.1)]">
                        {({ close }) => (
                          <LocationSelectPopover
                            locationPages={locationPages}
                            closePopover={close}
                            onSelectLocation={getLocationSelectDestination}
                            selectedLocationName={
                              effectiveLocationName || undefined
                            }
                          />
                        )}
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            )}

            {!bookingMode && (
              <button
                className="lg:hidden w-full h-full"
                onClick={() => {
                  preloadMobileMenuImages()
                  setMobileMenuState("open")
                }}
                title="Open menu"
              >
                <Menu className="text-white" />
              </button>
            )}
          </div>

          <div className="flex md:hidden items-center gap-2">
            <Link to="/" title="Home">
              {headerBrandLogo}
            </Link>
            {locationPages.length > 0 && (
              <Popover>
                {({ open, close }) => (
                  <>
                    <Popover.Button
                      ref={mobileLocationBtnRef}
                      className="flex items-center gap-1 text-white transition-opacity p-2 active:scale-[98%]"
                    >
                      {displayLocationName}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      />
                    </Popover.Button>
                    <LocationSelectPopover
                      locationPages={locationPages}
                      closePopover={close}
                      onSelectLocation={getLocationSelectDestination}
                      selectedLocationName={effectiveLocationName || undefined}
                      isOpen={open}
                    />
                  </>
                )}
              </Popover>
            )}
          </div>

          {bookingMode && <div className="flex-1 md:hidden" />}

          {!bookingMode && (
            <div className="hidden gap-4 text-white lg:flex">
              {cmsLocationSlug ? (
                <>
                  <HeaderLink
                    title="Things To Do"
                    url={`/${publicLocationSiteSlug}/things-to-do`}
                  />
                  <HeaderLink title="Plan a Party" url="/celebrations" />
                  <HeaderLink
                    title="Getting Here"
                    url={`/locations/${cmsLocationSlug}`}
                  />
                </>
              ) : (
                <>
                  <HeaderPopoverLink
                    title="Locations"
                    href="/visit"
                    popover={closePopover => (
                      <HeaderLocationsPopover
                        locationPages={locationPages}
                        closePopover={closePopover}
                        onSelectLocation={getLocationSelectDestination}
                      />
                    )}
                  />
                  {activityLinks.length > 0 && (
                    <HeaderPopoverLink
                      title="Things To Do"
                      popover={closePopover => (
                        <HeaderActivitiesPopover
                          links={activityLinks}
                          closePopover={closePopover}
                        />
                      )}
                    />
                  )}
                  <HeaderLink title="Deals" url="/deals" />
                  <HeaderLink title="Plan a Party" url="/celebrations" />
                </>
              )}
            </div>
          )}

          {bookingMode && (
            <div className="hidden md:flex flex-1 items-center justify-end">
              {bookingSignInDesktop}
            </div>
          )}

          {!bookingMode && (
            <div className="flex flex-1 items-center justify-end gap-8">
              {showSearch && (
                <>
                  <Link to="/search" className="xl:hidden" title="Search">
                    <Search className="text-white" />
                  </Link>

                  <div className="hidden xl:block">
                    <SearchInput
                      leftIcon={
                        <button
                          title="Search"
                          type="button"
                          onClick={() => goToSearchPage("")}
                          className="absolute top-1/2 z-10 hidden -translate-y-1/2 transform rounded-full pl-3 text-camp-primary transition-opacity duration-200 hover:opacity-70 xl:block"
                        >
                          <Loupe />
                        </button>
                      }
                      initialQuery={""}
                      onQueryChange={value => setQueryValue(value)}
                      onSearch={value => goToSearchPage(value)}
                      inputClass="flex-1 h-8 pl-10 rounded-2xl"
                      showClearButton
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {bookingMode && (
        <div className="md:hidden flex items-center justify-center bg-[#244482] px-4 py-3 shadow-sm">
          {bookingSignInMobile}
        </div>
      )}
    </>
  )
}

export default HeaderColorBarV2
